// Service-worker update controller (F-074 — reliable, non-disruptive updates).
//
// This module owns the browser-side wiring for applying a new build WITHOUT ever
// interrupting live scoring. The pure decisions it depends on live in
// ./swUpdate.js (unit-tested in node); everything here touches window / the SW
// registration and so is intentionally kept out of the test import graph.
//
// Two problems it solves:
//
// 1. Mid-round activation crash (root cause). vite-plugin-pwa previously ran in
//    `autoUpdate` mode with `skipWaiting` — a new build's SW activated the
//    instant it was found, INCLUDING while the user was mid-round on the
//    scorecard (JS still on build-A, reload deferred). Activating build-B's SW
//    PURGES build-A's precache, so any later lazy chunk (e.g. the precached
//    ManualHoleMap, or the network-fetched HoleMap) 404s and the rejected
//    dynamic import bubbles to the ROOT ErrorBoundary, replacing the whole
//    scorecard with the full-screen "Something went wrong" card mid-round.
//    Fix: run in `prompt` mode (workbox `skipWaiting:false`) so a new SW
//    INSTALLS but WAITS. Build-A stays in control and serves ALL of build-A's
//    precached assets until we message the waiting SW to activate at a SAFE
//    moment (foreground on a safe surface, in-app nav to a safe surface, or a
//    cold launch which always gets the newest build).
//
// 2. A purged / missing lazy chunk must never crash the scorecard (defense in
//    depth). The GPS HoleMap is globIgnored (network-fetched, not precached), so
//    build-A's HoleMap-<hash>.js can 404 after a new deploy REGARDLESS of SW
//    state. We listen for Vite's `vite:preloadError`: on a safe surface we
//    preventDefault + reload (fetch the current build's chunk); on a protected
//    surface we preventDefault (do NOT let it crash), keep the scorecard alive
//    (a local map error boundary in ScorecardPage renders a small "map
//    unavailable" fallback), fire a soft non-blocking notice, and defer the
//    reload to the next safe moment.
import { registerSW } from 'virtual:pwa-register'
import { Capacitor } from '@capacitor/core'
import { shouldApplyPendingUpdate, handlePreloadError } from './swUpdate'

// A new build's SW is installed and WAITING (not yet activated — build-A's
// precache is still intact and serving the live round).
let newWorkerWaiting = false
// A lazy-chunk load failed on a protected surface; reload at the next safe moment.
let deferredPreloadReload = false
// The updateSW() function returned by registerSW — messaging the waiting SW to
// skipWaiting, which then becomes controlling and reloads the page.
let updateSW = null

// Apply a waiting/deferred update IF the user is now on a safe (non-round)
// surface. Called on foreground, on in-app navigation to a safe path, and right
// after a new SW is found. A cold launch always fetches the newest build, so
// worst case a deferred update lands on next app open.
export function applyPendingUpdateIfSafe() {
  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  const pending = newWorkerWaiting || deferredPreloadReload
  if (!shouldApplyPendingUpdate(pending, path)) return

  if (newWorkerWaiting && typeof updateSW === 'function') {
    // Activate the waiting SW: messageSkipWaiting() → the SW's 'controlling'
    // listener reloads the page onto the new build. Clear both flags first so
    // this can't re-enter.
    newWorkerWaiting = false
    deferredPreloadReload = false
    updateSW(true)
    return
  }

  // A chunk 404'd but no SW is waiting yet — a plain reload fetches the current
  // build's index.html (NetworkFirst) and its fresh chunks.
  deferredPreloadReload = false
  window.location.reload()
}

// FIX B (native shell): the Workbox PWA service worker must NOT run inside the
// Capacitor WebView. It is registered at scope https://localhost/ where it both
// (a) errors on precache (favicon.ico 404 → bad-precaching-response,
// InvalidStateError) and (b) intercepts the app's backend calls with empty
// cached responses — which is a second cause of the post-login splash hang.
// Native serves the bundled dist/ assets directly and needs no PWA SW. On web
// this branch is never taken (isNativePlatform() === false) so F-074 prompt-mode
// registration below is completely unchanged.
async function unregisterNativeServiceWorker() {
  try {
    if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
      const regs = await navigator.serviceWorker.getRegistrations()
      for (const r of regs) { try { await r.unregister() } catch {} }
    }
    // Clear any caches a previously-installed (broken) SW left behind so a
    // device that already cached it recovers on this launch.
    if (typeof caches !== 'undefined' && caches.keys) {
      const keys = await caches.keys()
      for (const k of keys) { try { await caches.delete(k) } catch {} }
    }
  } catch {}
}

// Wire up the service-worker registration + update lifecycle. Called once from
// main.jsx at boot.
export function registerServiceWorker() {
  // Native shell: skip PWA SW registration entirely and best-effort clean up any
  // SW/caches a prior build left in the WebView. updateSW stays null, so
  // applyPendingUpdateIfSafe() (called from App.jsx on nav) sees no pending
  // update and early-returns — a safe no-op.
  if (Capacitor.isNativePlatform()) {
    unregisterNativeServiceWorker()
    return
  }

  updateSW = registerSW({
    immediate: true,
    onRegisteredSW(swUrl, registration) {
      if (!registration) return
      // Actively check for a new SW: on an interval and whenever the user
      // returns to the tab. These run for the app's lifetime by design.
      const check = () => { try { registration.update() } catch {} }
      setInterval(check, 30 * 60 * 1000)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return
        check()
        // If an update is waiting (or a chunk load was deferred) because the
        // user was mid-round, apply it now that they may be back on a safe
        // surface.
        applyPendingUpdateIfSafe()
      })
    },
    // Prompt mode: a new build's SW has INSTALLED and is WAITING — it has NOT
    // activated, so build-A's precache stays intact and the live round is fully
    // served by the old SW. Apply immediately if on a safe surface; otherwise
    // keep it waiting and apply at the next safe moment. No modal, no
    // interstitial, no upsell (ad-policy safe).
    onNeedRefresh() {
      newWorkerWaiting = true
      applyPendingUpdateIfSafe()
    },
  })

  // Defense in depth for a failed lazy-chunk load (see header, problem 2).
  if (typeof window !== 'undefined') {
    window.addEventListener('vite:preloadError', (e) => {
      const path = window.location.pathname
      if (handlePreloadError(path) === 'reload') {
        // Safe surface — swallow the error and reload onto the current build.
        e.preventDefault()
        window.location.reload()
        return
      }
      // Protected surface (mid-round) — do NOT let the failure crash the
      // scorecard. Keep it mounted (the local map error boundary renders the
      // fallback), surface a soft notice, and defer the reload.
      e.preventDefault()
      deferredPreloadReload = true
      try { window.dispatchEvent(new CustomEvent('tf:update-pending')) } catch {}
    })
  }
}
